import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Asset } from '@shared/types'
import {
  changePictureTags,
  readPictureTags,
  type AdminLibrary,
  type PictureTagsView,
  type ReportTag,
} from '@shared/services/libraryAdminService'
import './pictureTags.css'

/**
 * One picture's tags, category by category (Huanrui, 2026-10-12)
 * -------------------------------------------------------------
 * Opened by an administrator holding the mouse on a picture for three seconds
 * in any library page but Amazon (LibraryPage, AssetCard's `onLongPress`).
 *
 * Every category of the library is a row: the tags the picture carries, each
 * with an x to take it off, and a box to add one -- pick an existing tag from
 * the list, or type a new name and press Enter to make it in that category.
 * Each change is saved at once, the way Reported works on the Auto Filter
 * page, and with the same meaning: put on by hand, Auto Filter leaves it; taken
 * off by hand, Auto Filter never puts it back.
 */
export function PictureTagsDialog({
  library,
  asset,
  onClose,
}: {
  library: AdminLibrary
  asset: Asset
  /** `changed` is true when anything was saved, so the filter can recount. */
  onClose: (changed: boolean) => void
}) {
  const [view, setView] = useState<PictureTagsView | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const changed = useRef(false)

  useEffect(() => {
    let live = true
    readPictureTags(library, asset.id)
      .then((next) => live && setView(next))
      .catch((caught: unknown) => live && setError(caught instanceof Error ? caught.message : 'The tags could not be read'))
    return () => {
      live = false
    }
  }, [library, asset.id])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(changed.current)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const send = (change: Parameters<typeof changePictureTags>[2], optimistic: (on: ReportTag[]) => ReportTag[]) => {
    if (!view) return
    const before = view
    setView({ ...view, on: optimistic(view.on) })
    setBusy(true)
    setError('')
    changePictureTags(library, asset.id, change)
      .then((next) => {
        changed.current = true
        setView(next)
      })
      .catch((caught: unknown) => {
        setView(before)
        setError(caught instanceof Error ? caught.message : 'That did not save')
      })
      .finally(() => setBusy(false))
  }

  const takeOff = (tag: ReportTag) => send({ remove: [tag.id] }, (on) => on.filter((one) => one.id !== tag.id))

  const putOn = (group: string, typed: string) => {
    if (!view) return
    const name = typed.trim().replace(/\s+/g, ' ')
    if (!name) return
    const existing = view.tags.find((tag) => tag.group === group && tag.name.toLowerCase() === name.toLowerCase())
    if (existing && view.on.some((one) => one.id === existing.id)) return
    const shown: ReportTag = {
      id: existing?.id ?? `new:${group}:${name}`,
      name: existing?.name ?? name,
      group,
      source: 'manual',
      pending: !existing,
    }
    send(existing ? { add: [existing.id] } : { create: [{ group, name }] }, (on) => [...on, shown])
  }

  const known = useMemo(() => new Set(view?.groups.map((group) => group.slug) ?? []), [view])
  const loose = view ? view.on.filter((tag) => !known.has(tag.group)) : []

  return createPortal(
    <div
      className="ptags"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose(changed.current)
      }}
    >
      <div className="ptags__card" role="dialog" aria-modal="true" aria-label={`Tags of ${asset.title}`}>
        <header className="ptags__head">
          <div>
            <h2 className="ptags__title">Edit tags</h2>
            <p className="ptags__sub">{asset.title}</p>
          </div>
          <span className="ptags__state">{busy ? 'Saving…' : view ? 'Saved as you go' : ''}</span>
          <button type="button" className="ptags__close" onClick={() => onClose(changed.current)} aria-label="Close">
            ×
          </button>
        </header>

        <div className="ptags__body">
          <div className="ptags__picture">
            {asset.thumbnailPath || asset.originalPath ? (
              <img src={asset.thumbnailPath || asset.originalPath} alt={asset.title} draggable={false} />
            ) : null}
          </div>

          <div className="ptags__groups">
            {!view && !error ? <p className="ptags__note">Reading the tags…</p> : null}
            {view?.groups.length === 0 ? <p className="ptags__note">This library has no categories yet.</p> : null}
            {view?.groups.map((group) => (
              <GroupRow
                key={group.slug}
                label={group.label}
                here={view.on.filter((tag) => tag.group === group.slug)}
                choices={view.tags.filter((tag) => tag.group === group.slug && !view.on.some((one) => one.id === tag.id))}
                disabled={busy}
                onRemove={takeOff}
                onAdd={(name) => putOn(group.slug, name)}
              />
            ))}
            {loose.length > 0 ? (
              <GroupRow label="Other" here={loose} choices={[]} disabled={busy} onRemove={takeOff} />
            ) : null}
            {error ? <p className="ptags__error">{error}</p> : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function GroupRow({
  label,
  here,
  choices,
  disabled,
  onRemove,
  onAdd,
}: {
  label: string
  here: ReportTag[]
  choices: { id: string; name: string }[]
  disabled: boolean
  onRemove: (tag: ReportTag) => void
  /** Absent for the row of tags in no listed category: those can only come off. */
  onAdd?: (name: string) => void
}) {
  const [typed, setTyped] = useState('')
  const listId = useMemo(() => `ptags-${Math.random().toString(36).slice(2)}`, [])
  const add = (name: string) => {
    if (!onAdd || !name.trim()) return
    onAdd(name)
    setTyped('')
  }
  const isNew = typed.trim() !== '' && !choices.some((one) => one.name.toLowerCase() === typed.trim().toLowerCase())

  return (
    <div className="ptags__group">
      <span className="ptags__label">{label}</span>
      <div className="ptags__chips">
        {here.map((tag) => (
          <span
            key={tag.id}
            className={`ptags__chip${tag.source === 'ai' ? ' ptags__chip--ai' : ''}${tag.pending ? ' ptags__chip--pending' : ''}`}
            title={tag.source === 'ai' ? 'Put on by Auto Filter' : 'Put on by hand'}
          >
            {tag.name}
            {tag.source === 'ai' ? <em>AI</em> : null}
            <button
              type="button"
              className="ptags__x"
              disabled={disabled || tag.id.startsWith('new:')}
              onClick={() => onRemove(tag)}
              aria-label={`Take ${tag.name} off`}
            >
              ×
            </button>
          </span>
        ))}
        {here.length === 0 ? <span className="ptags__none">None</span> : null}
        {onAdd ? (
          <span className="ptags__adder">
            <input
              className="ptags__input"
              value={typed}
              list={listId}
              placeholder="+ Add"
              disabled={disabled}
              aria-label={`Add a ${label} tag`}
              onChange={(event) => {
                const value = event.target.value
                // Picked from the list (not typed): add at once.
                const native = event.nativeEvent as InputEvent
                const picked = !native.inputType || native.inputType === 'insertReplacementText'
                if (picked && choices.some((one) => one.name === value)) add(value)
                else setTyped(value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  add(typed)
                }
              }}
            />
            <datalist id={listId}>
              {choices.map((one) => (
                <option key={one.id} value={one.name} />
              ))}
            </datalist>
            {isNew ? (
              <button type="button" className="ptags__make" disabled={disabled} onClick={() => add(typed)}>
                Create “{typed.trim()}”
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
    </div>
  )
}
